import { useCallback, useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import type { ProvenanceEntry, ProvenanceMap } from '@/modules/inventory/types/boat.types';
import type { ScalarValue } from '@/modules/inventory/utils/boat-changes';
import { CANVAS_SCALAR_KEYS, toFormScalar, type CanvasScalarKey } from '../lib/canvasEditable';
import { engineFormPath } from '../lib/engineProvenanceKey';
import type { CanvasHydration } from './useCanvasHydration';
import { useFieldReview } from './useFieldReview';
import { useProvenanceSync } from './useProvenanceSync';

interface UseCanvasFieldReviewArgs {
  methods: Pick<UseFormReturn<WizardFormData>, 'getValues' | 'setValue'>;
  boatId: string | null;
  fieldSources: ProvenanceMap | undefined;
  hydration: Pick<CanvasHydration, 'refresh' | 'getSnapshot'>;
}

export interface CanvasFieldReview {
  /** "Use it": the dealer chooses `value` for `field`. */
  acceptSuggestion: (field: string, fieldSourceId: string, value: unknown) => void;
  /** "Dismiss" / "Keep this value": the current value stays, the flag is cleared. */
  dismissFlag: (fieldSourceIds: string[]) => void;
  /**
   * #1317 — the dealer picked `entry` in the field's ConflictChooser. The form
   * already holds its value; the server confirms the row (demoting an earlier
   * pick) and the canvas re-reads the boat, as "Use it" does. A failed or
   * skipped apply calls `restore`.
   */
  pickSource: (field: string, entry: ProvenanceEntry, restore?: () => void) => void;
  /** Flags the dealer has decided, hidden until the field no longer reports them. */
  hiddenFlagIds: ReadonlySet<string>;
}

function isScalarKey(field: string): field is CanvasScalarKey {
  return (CANVAS_SCALAR_KEYS as readonly string[]).includes(field);
}

/** The field-source ids the form's `_provenance` still flags for review. */
function flaggedIds(provenance: ProvenanceMap | undefined): Set<string> {
  const ids = new Set<string>();
  for (const raw of Object.values(provenance ?? {})) {
    for (const entry of (Array.isArray(raw) ? raw : [raw]) as ProvenanceEntry[]) {
      if (entry?.needsReview && entry.id) ids.add(entry.id);
    }
  }
  return ids;
}

/**
 * #1182 / S4-45 / S4-46 — the canvas's decisions on review flags.
 *
 * - "Use it" is the dealer choosing that value for that field: once the apply
 *   succeeds the value is written into the form (dirty), then the boat is re-read.
 *   The rebase then finds the form equal to the server, so nothing is left to save,
 *   even when the dealer had typed in the field within the autosave debounce. A
 *   failed apply writes nothing (the toast asks to retry).
 * - A decided flag is hidden by its field-source id at once, and stays hidden until
 *   the field's `_provenance` no longer flags that id. A field holding an unsaved
 *   value skips the provenance sync, so without this its notice would stick and
 *   offer a second click that does nothing.
 */
export function useCanvasFieldReview({ methods, boatId, fieldSources, hydration }: UseCanvasFieldReviewArgs): CanvasFieldReview {
  const queryClient = useQueryClient();
  const refresh = hydration.refresh;
  const onReviewed = useCallback(() => {
    if (boatId) queryClient.invalidateQueries({ queryKey: ['field-sources', boatId] });
    void refresh();
  }, [boatId, queryClient, refresh]);
  const { accept, dismiss } = useFieldReview(boatId, onReviewed);
  const [hiddenFlagIds, setHiddenFlagIds] = useState<ReadonlySet<string>>(() => new Set());

  const hide = useCallback((ids: string[]) => {
    setHiddenFlagIds((current) => new Set([...current, ...ids]));
  }, []);
  const unhide = useCallback((ids: string[]) => {
    setHiddenFlagIds((current) => new Set([...current].filter((id) => !ids.includes(id))));
  }, []);

  // Declared before the prune below, so the prune reads `_provenance` after this sync.
  useProvenanceSync(methods, fieldSources, hydration.getSnapshot);

  useEffect(() => {
    setHiddenFlagIds((current) => {
      if (current.size === 0) return current;
      // `_provenance` is runtime-only form state (WizardFormData does not declare it).
      const form = methods.getValues() as unknown as { _provenance?: ProvenanceMap };
      const stillFlagged = flaggedIds(form._provenance);
      const kept = [...current].filter((id) => stillFlagged.has(id));
      return kept.length === current.size ? current : new Set(kept);
    });
    // Runs when the projection changes, right after the sync has copied it into the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSources]);

  const acceptSuggestion = useCallback(
    (field: string, fieldSourceId: string, value: unknown) => {
      hide([fieldSourceId]);
      accept([fieldSourceId], {
        onDone: () => {
          // #1307 — an engine key lands on the engine row's form field.
          const target = field.startsWith('engine#') ? engineFormPath(field) : field;
          if (target === null) return;
          const formValue = isScalarKey(field) ? toFormScalar(field, (value ?? null) as ScalarValue) : value;
          methods.setValue(target as never, formValue as never, { shouldDirty: true });
        },
        onFailed: () => unhide([fieldSourceId]),
      });
    },
    [accept, hide, unhide, methods],
  );

  const pickSource = useCallback(
    (_field: string, entry: ProvenanceEntry, restore?: () => void) => {
      // A failed apply, or one that reports the row skipped (useFieldReview treats
      // an all-skipped result as failure), undoes the pick so autosave does not
      // record the unconfirmed value as the dealer's edit.
      if (entry.id) accept([entry.id], { onFailed: restore });
    },
    [accept],
  );

  const dismissFlag = useCallback(
    (fieldSourceIds: string[]) => {
      hide(fieldSourceIds);
      dismiss(fieldSourceIds, { onFailed: () => unhide(fieldSourceIds) });
    },
    [dismiss, hide, unhide],
  );

  return { acceptSuggestion, dismissFlag, pickSource, hiddenFlagIds };
}
